import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {conditionOutputSchema} from '../../promptToCardAgentV2.schemas';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import validateConditionOutput from '../../utils/conditions/validateConditionOutput';
import {ConditionNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import {ConditionOperatorKey} from 'common/src/modules/conditions/conditions.types';
import {DateTime} from 'luxon';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import _findLastIndex from 'lodash/findLastIndex';

const addConditionOutputToolSchema = z.object({
  conditionOutput: conditionOutputSchema.extend({
    name: z.string().describe('The name of the output'),
  }),
});

export default function addConditionOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof addConditionOutputToolSchema, ConditionCardScratchpadState>,
  ) {
    const {conditionOutput} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad =
      validateAgentScratchpad<ConditionCardScratchpadState>(agentScratchpad, NodeType.CONDITION);

    const error = validateConditionOutput(
      conditionOutput,
      validatedScratchpad.card.condKeyType as MacroItemTypeValue,
      validatedScratchpad.card.condOperator as ConditionOperatorKey,
    );

    if (error) {
      return toolResult(error);
    }

    // Store date values as ISO strings in scratchpad to avoid serialization issues
    // They will be converted to DateTime objects when finalizing
    const newOutput: ConditionNodeOutputData = {
      id: validatedScratchpad.card.outputs.length + 1,
      nodeId: NO_NODE_ID,
      name: conditionOutput.name,
      type: OutputType.Regular,
      ...([MacroItemTypeValue.TextList, MacroItemTypeValue.Text].includes(
        validatedScratchpad.card.condKeyType as MacroItemTypeValue,
      ) && {textValues: conditionOutput.textValues}),
      ...(validatedScratchpad.card.condKeyType === MacroItemTypeValue.Numeric && {
        numericValues: conditionOutput.numericValues,
      }),
      ...(validatedScratchpad.card.condKeyType === MacroItemTypeValue.Condition && {
        booleanValue: conditionOutput.booleanValue,
      }),
      ...([MacroItemTypeValue.Date, MacroItemTypeValue.DateList].includes(
        validatedScratchpad.card.condKeyType as MacroItemTypeValue,
      ) &&
        validatedScratchpad.card.condOperator !== ConditionOperatorKey.Between &&
        !!conditionOutput.dateValues && {
          dateValues: conditionOutput.dateValues.map((date) => DateTime.fromISO(date)),
        }),
      ...([MacroItemTypeValue.Date, MacroItemTypeValue.DateList].includes(
        validatedScratchpad.card.condKeyType as MacroItemTypeValue,
      ) &&
        validatedScratchpad.card.condOperator === ConditionOperatorKey.Between &&
        !!conditionOutput.datesRange && {
          datesRange: {
            from: DateTime.fromISO(conditionOutput.datesRange.from),
            to: DateTime.fromISO(conditionOutput.datesRange.to),
          },
        }),
      ...(validatedScratchpad.card.condKeyType === MacroItemTypeValue.Numeric &&
        validatedScratchpad.card.condOperator !== ConditionOperatorKey.Between &&
        !!conditionOutput.numericValues && {
          numericValues: conditionOutput.numericValues,
        }),
      ...(validatedScratchpad.card.condKeyType === MacroItemTypeValue.Numeric &&
        validatedScratchpad.card.condOperator === ConditionOperatorKey.Between &&
        !!conditionOutput.numbersRange && {
          numbersRange: conditionOutput.numbersRange,
        }),
    } as ConditionNodeOutputData;

    // Find the last index of Regular outputs to insert the new output there
    const lastRegularOutputIndex = _findLastIndex(
      validatedScratchpad.card.outputs,
      (output) => output.type === OutputType.Regular,
    );

    if (lastRegularOutputIndex === -1) {
      // No Regular outputs exist, insert at the beginning
      validatedScratchpad.card.outputs.unshift(newOutput);
    } else {
      // Insert after the last Regular output
      validatedScratchpad.card.outputs.splice(lastRegularOutputIndex + 1, 0, newOutput);
    }

    return toolResult('Condition output added');
  }

  return tool<typeof addConditionOutputToolSchema, ConditionCardScratchpadState>({
    name: V2CardAgentToolName.AddConditionOutput,
    displayName: 'Adding condition output',
    description: 'Add a condition output to a condition card',
    inputSchema: addConditionOutputToolSchema,
    execute,
  });
}
